import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, tokenStore } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, KeyRound, User, Mail } from "lucide-react";

const ROLE_LABEL = {
  owner: "Owner",
  administrative: "Administrative",
  call_center: "Call center / CSR",
  technician: "Technician",
};

/**
 * Public invite-acceptance page: /{slug}/invite/{token}
 * - Fetches invite preview server-side (no auth, throws 4xx on invalid/expired/consumed)
 * - Recipient sets name + password (email if invite is open) → user + tenant_id + role pre-assigned
 * - Auto-logs the new user in and redirects to the tenant dashboard
 */
export default function Invite() {
  const { slug, token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [submitErr, setSubmitErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Public preview — do not include Authorization header (avoid cross-tenant token leaks)
    axios.get(`${API}/invites/token/${token}`)
      .then(({ data }) => setPreview(data))
      .catch((e) => setLoadErr(e?.response?.data?.detail || "This invite link is invalid or expired."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr(null);
    if (!form.name.trim()) { setSubmitErr("Name is required"); return; }
    if (form.password.length < 8) { setSubmitErr("Password must be at least 8 characters"); return; }
    if (!/\d/.test(form.password)) { setSubmitErr("Password must include at least one number"); return; }
    if (!preview.email_locked && !form.email.trim()) { setSubmitErr("Email is required"); return; }
    setSubmitting(true);
    try {
      const body = { name: form.name, password: form.password };
      if (!preview.email_locked) body.email = form.email;
      const { data } = await axios.post(`${API}/invites/token/${token}/accept`, body);
      if (!data.access_token) throw new Error("No token returned");
      const targetSlug = data.tenant_slug || slug;
      tokenStore.setForSlug(targetSlug, data.access_token);
      await refresh();
      navigate(`/${targetSlug}/app/overview`, { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setSubmitErr(typeof d === "string" ? d : "Could not accept invite. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadErr) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a1428] p-6" data-testid="invite-error">
        <div className="max-w-[420px] bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="text-[13px] font-mono-data uppercase tracking-[.12em] text-[#991b1b] mb-2">Invite unavailable</div>
          <div className="font-display font-bold text-[22px] text-[#0e1420] mb-3">Link can&apos;t be used</div>
          <p className="text-[14px] text-[color:var(--cc-ink-muted)] mb-6">{loadErr}</p>
          <a href={`/${slug || ""}/login`} className="cc-btn-primary inline-flex text-[13px] py-2 px-4">Go to sign-in</a>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a1428]" data-testid="invite-loading">
        <div className="text-[13px] font-mono-data uppercase tracking-[.12em] text-white/60">Loading invite…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1428] flex items-center justify-center p-6" data-testid="invite-page">
      <div className="w-full max-w-[460px] bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-[#0a1428] text-white px-6 py-5">
          <div className="text-[11px] font-mono-data uppercase tracking-[.14em] text-[#FFC72C] font-bold">You&apos;re invited</div>
          <div className="font-display font-bold text-[22px] mt-1">{preview.tenant_name}</div>
          <div className="text-[12.5px] text-white/60 mt-0.5">{preview.tenant_location}</div>
        </div>
        <div className="px-6 pt-5 pb-6">
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="invite-meta">
            <span className="inline-flex items-center gap-1.5 text-[11.5px] font-mono-data uppercase tracking-widest bg-[#e7f4fb] text-[#1c6a99] border border-[#c5e0ee] rounded-full px-2.5 py-1 font-bold">
              <ShieldCheck size={12} strokeWidth={2} /> {ROLE_LABEL[preview.role] || preview.role}
            </span>
            {preview.email_locked ? (
              <span className="inline-flex items-center gap-1.5 text-[11.5px] font-mono-data bg-[#f5f6f8] text-[color:var(--cc-ink-muted)] border border-[#e7e9ee] rounded-full px-2.5 py-1">
                <Mail size={12} strokeWidth={2} /> {preview.locked_email}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[11.5px] font-mono-data uppercase tracking-widest bg-[#fff6dc] text-[#8a6a1c] border border-[#f0dfa5] rounded-full px-2.5 py-1 font-bold">
                Open link
              </span>
            )}
          </div>

          <form onSubmit={submit} className="grid gap-4">
            {!preview.email_locked && (
              <div>
                <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Your email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="invite-email"
                       className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2.5 text-[14px] outline-none focus:border-[#0072bc]" />
              </div>
            )}
            <div>
              <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Full name</label>
              <div className="relative">
                <User size={14} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--cc-ink-muted)]" />
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="invite-name"
                       className="w-full border border-[#e7e9ee] rounded-lg pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-[#0072bc]" />
              </div>
            </div>
            <div>
              <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Set a password</label>
              <div className="relative">
                <KeyRound size={14} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--cc-ink-muted)]" />
                <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                       data-testid="invite-password"
                       autoComplete="new-password"
                       className="w-full border border-[#e7e9ee] rounded-lg pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-[#0072bc]" />
              </div>
              <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-1.5">Minimum 8 characters, must include a number.</div>
            </div>
            {submitErr && <div className="text-[12.5px] text-[#991b1b] font-semibold" data-testid="invite-submit-error">{submitErr}</div>}
            <button type="submit" disabled={submitting} data-testid="invite-submit"
                    className="cc-btn-primary w-full text-[14px] py-2.5 mt-1 disabled:opacity-60">
              {submitting ? "Creating account…" : "Accept invite & sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
